// Field shapes for the registration forms.
//
// These were previously inferred from the initial-values object literal, which
// typed `companyName` as `string` and the empty arrays as `never[]` — neither of
// which matches what the fields actually hold once the user has filled them in.

// A company chosen from the searchable select. The field starts as '' before a
// selection has been made, hence the union at the use sites.
export interface CompanyOption {
  value: number | string;
  label: string;
}

// A bookable service offered by the organisation, as returned by
// /v1/organisations/services. Distinct from hooks/useClientServices'
// ClientService (a service the client has already registered), which carries
// `clinical` rather than `isClinical`.
export interface AvailableService {
  id: number;
  name: string;
  isClinical: boolean;
}

// A CCG region option for the commissioning-body select, mapped from
// /v1/organisations/ccg_regions.
export interface CommissioningBodyOption {
  label: string;
  value: number | string;
}

export interface RegistrationServiceFields {
  title: string;
  position: number;
  isOpen: boolean;
  serviceType: string;
  service: string;
  serviceIdentifier: string;
  // Radio groups whose values are the strings 'Yes'/'No'.
  isCommissionedService: string;
  serviceCommissioningBody: string;
  isLicensedForService: string;
  serviceLicensingBody: string;
  serviceRegistrationNo: string;
}

// The company half of the form, shared by new-client and full registration.
export interface ClientRegistrationFields {
  companyName: CompanyOption | '';
  address1: string;
  address2: string;
  town: string;
  county: string;
  postcode: string;
  companyType: string;
  services: RegistrationServiceFields[];
  // Service ids selected when joining a company that already exists.
  existingClientServices: Array<number | string>;
  // Checkboxes, so '' until ticked.
  agreePrivacy: string;
  agreeTerms: string;
}

export interface UserRegistrationFields extends ClientRegistrationFields {
  firstName: string;
  lastName: string;
  phoneNumber: string;
  mobileNumber: string;
  email: string;
  confirmEmail: string;
  password: string;
  confirmPassword: string;
  // Ticked marketing options accumulate off DOM checkbox events, so ids picked
  // in the browser arrive as strings alongside any numeric initial values.
  marketingOptions: Array<number | string>;
}

// `companyName` is '' until a company is picked; this narrows it so `.label` and
// `.value` reads are safe. Reading them off '' previously yielded undefined
// rather than throwing, which this preserves.
export const selectedCompany = (
  companyName: CompanyOption | ''
): CompanyOption | undefined =>
  typeof companyName === 'string' ? undefined : companyName;
